const extractLocaleFromPath = (path: string): string | undefined => {
  // Matches: /[2 letters] OR /[2 letters]-[2 letters]
  // Examples: /de/, /at/, /fr-ch/, /fr-be/
  const match = path.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/)
  return match?.[1]
}

/**
 * Returns a normalized route key for promotion attribution.
 * - Auto-detects and strips leading locale segment (e.g., /de, /fr-ch)
 * - Keeps path and query to distinguish filtered pages
 * - Drops fragment (#...)
 * - Optionally ignores specific query params (extend if needed)
 */
export const normalizeRouteKey = (
  fullPath: string,
  ignoreQueryKeys: string[] = [],
  locale?: string,
): string => {
  try {
    if (!fullPath) return '/'
    // Split hash
    const [pathAndQuery] = fullPath.split('#')
    // Separate path and query
    const [rawPath, rawQuery] = pathAndQuery.split('?')

    // Auto-detect locale from path if not explicitly provided
    const detectedLocale = locale || extractLocaleFromPath(rawPath)

    // Strip locale prefix if detected
    const path = detectedLocale && rawPath.startsWith(`/${detectedLocale}`)
      ? rawPath.replace(`/${detectedLocale}`, '') || '/'
      : rawPath || '/'

    if (!rawQuery) return path

    const params = new URLSearchParams(rawQuery)
    // Optionally drop noisy params (e.g., page)
    ignoreQueryKeys.forEach((k) => params.delete(k))

    const queryString = params.toString()
    return queryString ? `${path}?${queryString}` : path
  } catch {
    return fullPath || '/'
  }
}

export default normalizeRouteKey
